<template>
  <div ref="container" class="tw-relative">
    <!-- Button and badge container -->
    <div class="tw-relative tw-inline-block">
      <a-button 
        @click="isActive = !isActive" 
        icon="solar:bell-broken" 
        intent="icon" 
        icon-class="!tw-text-2xl" 
        class="tw-rounded-lg"
        :class="{'!tw-ring-1 !tw-ring-primary-200': unreadCount > 0}"
      />
      
      <!-- Notification badge, positioned outside the button -->
      <div 
        v-if="unreadCount > 0" 
        class="tw-absolute tw-top-0 tw-right-0 tw-min-w-5 tw-h-5 tw-px-1.5 tw-rounded-full tw-bg-red-500 tw-text-white tw-text-[10px] tw-flex tw-items-center tw-justify-center tw-font-medium tw-transform tw-translate-x-1/2 tw-translate-y-[-30%] tw-shadow-sm tw-z-10"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </div>
    </div>

    <transition
      class="tw-duration-200"
      enter-from-class="tw-translate-y-[10px] tw-opacity-0"
      leave-to-class="tw-translate-y-[10px] tw-opacity-0"
    >
      <div v-if="isActive">
        <div
          @click.stop
          :class="[$root.$i18n.locale == 'ar' ? 'tw-left-0' : 'tw-right-0']"
          class="tw-w-[320px] tw-max-h-[450px] tw-absolute -tw-bottom-1 tw-translate-y-full tw-bg-white tw-rounded-xl tw-shadow-lg tw-overflow-hidden"
        >
          <!-- Simple Header -->
          <div class="tw-p-3 tw-border-b tw-border-gray-100">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h3 class="tw-font-medium tw-text-gray-800">{{ t('common.notifications', 'Notifications') }}</h3>
              <button 
                v-if="notifications.length > 0" 
                @click="markAllAsRead" 
                class="tw-text-xs tw-text-primary-600 hover:tw-text-primary-700">
                {{ t('buttons.clear-all', 'Clear all') }}
              </button>
            </div>
          </div>
          
          <!-- Tabs -->
          <div class="tw-flex tw-border-b tw-border-gray-100">
            <button 
              @click="switchTab('all')"
              class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium"
              :class="[
                activeTab === 'all' 
                  ? 'tw-text-primary-600 tw-border-b-2 tw-border-primary-500' 
                  : 'tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
              ]">
              {{ t('common.all', 'All') }}
            </button>
            <button 
              @click="switchTab('unread')"
              class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium"
              :class="[
                activeTab === 'unread' 
                  ? 'tw-text-primary-600 tw-border-b-2 tw-border-primary-500' 
                  : 'tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
              ]">
              {{ t('common.unread', 'Unread') }}
            </button>
          </div>
          
          <!-- Notifications list -->
          <div 
            ref="scrollContainer" 
            class="tw-overflow-y-auto" 
            style="max-height: 350px;"
            @scroll="handleScroll"
          >
            <!-- Initial loading state -->
            <div v-if="loading" class="tw-flex tw-justify-center tw-items-center tw-py-4">
              <div class="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-primary-500 tw-rounded-full tw-border-t-transparent"></div>
            </div>
            
            <!-- Notifications -->
            <template v-else-if="filteredNotifications.length">
              <SingleNotification 
                v-for="notification in filteredNotifications" 
                :key="notification.id"
                :notification="notification"
                @notification-read="markAsRead"
                @popup-state-changed="handlePopupStateChange"
              />
              
              <!-- Intersection observer target element -->
              <div ref="intersectionTarget" class="tw-h-1"></div>
              
              <!-- Loading more indicator -->
              <div v-if="paginationState.isLoadingMore" class="tw-flex tw-justify-center tw-items-center tw-py-3">
                <div class="tw-animate-spin tw-h-4 tw-w-4 tw-border-2 tw-border-primary-500 tw-rounded-full tw-border-t-transparent"></div>
                <span class="tw-ml-2 tw-text-xs tw-text-gray-500">{{ t('common.loading', 'Loading...') }}</span>
              </div>
              
              <!-- End of results message -->
              <div v-else-if="!paginationState.hasMore && !paginationState.isLoadingMore" 
                   class="tw-py-4 tw-px-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-2">
                  <icon icon="solar:check-circle-broken" class="tw-text-gray-400 tw-text-lg" />
                </div>
                <p class="tw-text-xs tw-text-gray-500">{{ t('common.no-more-notifications', 'You\'ve reached the end') }}</p>
              </div>
              
              <!-- Error state -->
              <div v-if="paginationState.error" class="tw-py-3 tw-px-4 tw-text-center">
                <p class="tw-text-xs tw-text-red-500 tw-mb-2">{{ paginationState.error }}</p>
                <button 
                  @click="retryLoadMore" 
                  class="tw-text-xs tw-text-primary-600 hover:tw-text-primary-700 tw-underline">
                  {{ t('buttons.retry', 'Retry') }}
                </button>
              </div>
            </template>
            
            <!-- Empty state -->
            <div v-else class="tw-py-8 tw-px-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
              <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-3">
                <icon icon="solar:bell-broken" class="tw-text-gray-400 tw-text-xl" />
              </div>
              <p class="tw-text-gray-700 tw-font-medium">{{ t('common.no-notifications', 'No notifications') }}</p>
              <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ t('common.check-later', 'Check back later for updates') }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
  
<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useAuthStore } from '@/stores';
import useAlert from '@/composables/useAlert';
//import useNotification from '@/services/notification';
import SingleNotification from '@/components/notifications/SingleNotification.vue';

const { t } = useI18n();
const authStore = useAuthStore();
const alert = useAlert;
// Initialize notification service at the top level
//const notificationService = useNotification();

const isActive = ref(false);
const container = ref(null);
const scrollContainer = ref(null);
const intersectionTarget = ref(null);
const loading = ref(true);
const activeTab = ref('all');
const notifications = ref([]);
const unreadCount = ref(0);
const notificationManager = ref(null);
const isAnyPopupOpen = ref(false);

// Pagination state
const paginationState = ref({
  currentPage: 1,
  lastPage: 1,
  hasMore: false,
  totalNotifications: 0,
  isLoadingMore: false,
  error: null
});

// Intersection observer
let intersectionObserver = null;

// Track popup state changes
function handlePopupStateChange(isOpen) {
  isAnyPopupOpen.value = isOpen;
}

// Close dropdown when clicking outside, but only if no popup is open
onClickOutside(container, () => {
  if (!isAnyPopupOpen.value) {
    isActive.value = false;
  }
});

// Get only the notifications matching the active tab
const filteredNotifications = computed(() => {
  if (activeTab.value === 'unread') {
    return notifications.value.filter(notification => notification.unread);
  }
  return notifications.value;
});

// Switch between tabs and reset pagination
async function switchTab(tab) {
  if (activeTab.value !== tab) {
    activeTab.value = tab;
    
    if (notificationManager.value) {
      await notificationManager.value.setFilter(tab);
    }
  }
}

// Setup intersection observer for infinite scroll
async function setupIntersectionObserver() {
  await nextTick();
  
  if (!intersectionTarget.value) return;
  
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && paginationState.value.hasMore && !paginationState.value.isLoadingMore) {
        loadMoreNotifications();
      }
    },
    {
      root: scrollContainer.value,
      rootMargin: '50px',
      threshold: 0.1
    }
  );
  
  intersectionObserver.observe(intersectionTarget.value);
}

// Load more notifications
async function loadMoreNotifications() {
  if (notificationManager.value) {
    await notificationManager.value.loadMoreNotifications();
  }
}

// Retry loading more notifications
async function retryLoadMore() {
  if (notificationManager.value) {
    await notificationManager.value.loadMoreNotifications();
  }
}

// Handle scroll event (fallback for intersection observer)
function handleScroll(event) {
  // This is a fallback in case intersection observer doesn't work
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  
  if (scrollTop + clientHeight >= scrollHeight - 50) {
    if (paginationState.value.hasMore && !paginationState.value.isLoadingMore) {
      loadMoreNotifications();
    }
  }
}

// Initialize the notification system
onMounted(async () => {
  if (authStore.user && authStore.user.id) {
    await initNotificationManager();
  }
});

// Clean up listeners when component is unmounted
onUnmounted(() => {
  if (notificationManager.value) {
    // Remove event listeners
    notificationManager.value.removeAllEventListeners();
  }
  
  // Clean up intersection observer
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
});

// Initialize the notification manager
async function initNotificationManager() {
  try {
    console.log('Initializing notification manager...');
    // Import the notification manager dynamically to avoid SSR issues
    const { notificationManager: manager } = await import('@/services/notificationManager');
    notificationManager.value = manager;
    
    // Initialize with current user ID
    await notificationManager.value.init(authStore.user.id);
    
    // Add event listeners
    notificationManager.value.addEventListener('onNotificationReceived', handleNewNotification);
    notificationManager.value.addEventListener('onNotificationUpdated', handleNotificationUpdate);
    notificationManager.value.addEventListener('onNotificationDeleted', handleNotificationDeletion);
    notificationManager.value.addEventListener('onCountUpdated', handleCountUpdate);
    notificationManager.value.addEventListener('onPaginationStateChanged', handlePaginationStateChange);
    
    // Set initial notifications from the manager
    notifications.value = notificationManager.value.notifications;
    console.log('Notifications loaded:', notifications.value);
    
    // Set unread count and finish loading
    unreadCount.value = notificationManager.value.unreadCount;
    loading.value = false;
    
    // Setup intersection observer after notifications are loaded
    setupIntersectionObserver();
  } catch (error) {
    console.error('Failed to initialize notification manager:', error);
    loading.value = false;
    // Just show empty state instead of demo data
    notifications.value = [];
    unreadCount.value = 0;
  }
}

// Handle new notification
function handleNewNotification(notification) {
  // Check if notification already exists to prevent duplicates
  const existingIndex = notifications.value.findIndex(n => n.id === notification.id);
  if (existingIndex !== -1) {
    console.log(`Notification with ID ${notification.id} already exists in component, updating instead of adding new`);
    notifications.value[existingIndex] = notification;
    return;
  }
  
  // Add to the beginning of the array
  notifications.value = [notification, ...notifications.value];
  
  // Show a toast for new notifications
  if (notification.type) {
    // Check for type to display correct alert variant
    const alertType = notification.type === 'danger' ? 'danger' : 
                     notification.type === 'error' ? 'danger' : notification.type;
    alert(notification.message, alertType);
  }
}

// Handle notification update
function handleNotificationUpdate(updatedNotification) {
  const index = notifications.value.findIndex(n => n.id === updatedNotification.id);
  if (index !== -1) {
    notifications.value[index] = updatedNotification;
  }
}

// Handle notification deletion
function handleNotificationDeletion(deletedNotification) {
  notifications.value = notifications.value.filter(n => n.id !== deletedNotification.id);
}

// Handle unread count update
function handleCountUpdate(count) {
  unreadCount.value = count;
}

// Handle pagination state changes
function handlePaginationStateChange(state) {
  paginationState.value = { ...state };
  
  // Update notifications from manager
  if (notificationManager.value) {
    notifications.value = notificationManager.value.notifications;
  }
  
  // Setup intersection observer when pagination state changes
  if (state.hasMore && !intersectionObserver) {
    setupIntersectionObserver();
  }
}

// Mark all notifications as read
function markAllAsRead() {
  if (notificationManager.value) {
    notificationManager.value.markAllAsRead();
  } else {
    // Just reset in case manager is not available
    notifications.value = notifications.value.map(notification => ({
      ...notification,
      unread: false
    }));
    unreadCount.value = 0;
  }
}

// Mark a single notification as read
function markAsRead(notificationId) {
  if (notificationManager.value) {
    notificationManager.value.markAsRead(notificationId);
  } else {
    // Update local copy if manager not available
    const index = notifications.value.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      notifications.value[index].unread = false;
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
  }
}
</script>
  
<style>
</style>